import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ApiError } from '@ynab/api-client'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './App.tsx'
import { AuthProvider, signOutToLogin, useAuth } from './lib/auth.tsx'
import { SignIn } from './views/SignIn.tsx'
import './styles.css'

/**
 * A 401 is a signed-out state, not an error to render.
 *
 * Once the refresh token is gone — expired, revoked, or signed out in another tab —
 * every query in flight fails at once, and without this the app paints a screenful of
 * "Not signed in." where the budget was. Sending it back through the sign-in screen
 * says the true thing instead, and re-authenticating usually needs no interaction
 * because the Kinde session cookie outlives the access token.
 *
 * Only 401. A 403 means the opposite — Kinde vouched for you and `members` did not —
 * and bouncing that to a login would loop forever against a door that opens onto the
 * same refusal.
 */
const onUnauthorised = (error: unknown) => {
  if (error instanceof ApiError && error.status === 401) signOutToLogin()
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: onUnauthorised }),
  mutationCache: new MutationCache({ onError: onUnauthorised }),
  defaultOptions: {
    queries: {
      // A budget only changes when you or a sync changes it, so background
      // refetching on an interval would be noise. Refetch when the window
      // regains focus instead — that is when a cron sync may have run.
      staleTime: 30_000,
      refetchOnWindowFocus: true,
      // Never retry an auth failure: the second attempt carries the same dead token
      // and only delays the sign-in screen by a round trip.
      retry: (count, error) =>
        error instanceof ApiError && (error.status === 401 || error.status === 403)
          ? false
          : count < 1,
    },
  },
})

/**
 * Nothing renders until we know who is asking.
 *
 * Not merely cosmetic: `App` fires a budget query on mount, and mounting it before
 * the token exists would put a guaranteed 401 in the query cache and paint an error
 * state on top of a working app for the first second of every cold load.
 *
 * The blank hold is deliberate too. `signedIn` is null only for the moment the SDK
 * takes to answer from a cookie already in the browser, and a spinner that appears
 * and vanishes inside 200ms reads as a flicker rather than as progress.
 */
function Gate() {
  const { signedIn } = useAuth()
  if (signedIn === null) return null
  return signedIn ? <App /> : <SignIn />
}

const root = document.getElementById('root')
if (!root) throw new Error('Missing #root')

createRoot(root).render(
  <StrictMode>
    <AuthProvider>
      <QueryClientProvider client={queryClient}>
        <Gate />
      </QueryClientProvider>
    </AuthProvider>
  </StrictMode>,
)
